import { get, post } from '../utils/networking';
import { PublicSurvey, TeamHealthMetrics } from './queries';
import {
  ActionPlanAggregatedSchema,
  FocusCategorySchema,
  ActionPlanDtoSchema,
  type FocusReason,
} from './schemas/action-plan.schema';
import { z } from 'zod';

export const fetchPublicSurveyByUUID = async (
  uuid: string,
): Promise<PublicSurvey> => await get(`/api/dashboard/public/surveys/${uuid}`);

export const fetchPublicTeamHealthMetricsByUUID = async (
  uuid: string,
): Promise<TeamHealthMetrics[]> =>
  await get(`/api/dashboard/public/surveys/${uuid}/team-health-metrics`);

// Action Plan API
export interface FocusCategory {
  categoryId: number;
  categoryName: string;
  score: number;
  standardDeviation?: number;
  reason: string;
  /** Missing from a backend older than the key */
  reasonKey?: FocusReason;
  nextSurveyDate: Date | null;
}

export interface ActionPlanItem {
  id: number;
  text: string;
}

export interface ActionPlanTemplate {
  category: string;
  icon: string;
  commitments: ActionPlanItem[];
  expectations: ActionPlanItem[];
}

export interface CreateActionPlanRequest {
  categoryId: number;
  commitmentItemIds: number[];
  expectationItemIds: number[];
  contextNotes?: string;
  tenureSegment?: string;
  roleSegment?: string;
}

export interface ActionPlan {
  id: string;
  surveyId: number;
  nextSurveyId?: number;
  categoryId: number;
  commitments: string[];
  expectations: string[];
  createdAt: Date;
  tenantId: number;
}

export interface CommitmentAggregation {
  statement: string;
}

export interface ExpectationAggregation {
  statement: string;
}

export interface PairedActionItem {
  sortOrder: number;
  commitment?: CommitmentAggregation;
  expectation?: ExpectationAggregation;
}

export interface SegmentBreakdown {
  segment: string;
  count: number;
}

export interface BaselineData {
  previousScore: number;
  currentScore: number;
  scoreChange: number;
  scoreTrend: 'up' | 'down' | 'stable';
}

export interface ActionRecommendation {
  number: number;
  title: string;
  content: string[];
  example?: string;
}

export interface ActionPlanRecommendations {
  recommendations: ActionRecommendation[];
}

export interface InterpretiveSummary {
  paragraphs: string[];
}

export interface ActionPlanAggregated {
  surveyId: number;
  categoryId: number;
  categoryName: string;
  participantCount: number;
  teamMemberCount: number;
  interpretiveSummary?: InterpretiveSummary;
  actionRecommendations?: ActionPlanRecommendations;
  pairedItems: PairedActionItem[];
  createdAt: Date;
  nextSurveyDate?: Date;
  baseline?: BaselineData;
  tenureSegments?: SegmentBreakdown[];
  roleSegments?: SegmentBreakdown[];
}

export const fetchActionPlanFocusCategory = async (
  uuid: string,
): Promise<FocusCategory> => {
  const data = await get(`/api/action-plan/public/surveys/${uuid}/action-plan/focus`);

  try {
    const validated = FocusCategorySchema.parse(data);
    return {
      ...validated,
      nextSurveyDate: validated.nextSurveyDate ? new Date(validated.nextSurveyDate) : null,
    };
  } catch (error) {
    console.error('Focus category validation failed:', error);
    if (error instanceof z.ZodError) {
      console.error('Validation errors:', error.errors);
    }
    throw new Error('Invalid API response format for focus category');
  }
};

export const fetchActionPlanTemplate = async (
  uuid: string,
  categoryId: number,
): Promise<ActionPlanTemplate> =>
  await get(`/api/action-plan/public/surveys/${uuid}/action-plan/template/${categoryId}`);

export const createActionPlan = async (
  uuid: string,
  data: CreateActionPlanRequest,
): Promise<ActionPlan> => {
  const response = await post(`/api/action-plan/public/surveys/${uuid}/action-plan`, data);

  try {
    const validated = ActionPlanDtoSchema.parse(response);
    return {
      id: validated.id,
      surveyId: validated.surveyId,
      nextSurveyId: validated.nextSurveyId,
      categoryId: validated.categoryId,
      commitments: validated.commitments || [],
      expectations: validated.expectations || [],
      createdAt: new Date(validated.createdAt),
      tenantId: validated.tenantId || 0,
    };
  } catch (error) {
    console.error('Action plan validation failed:', error);
    if (error instanceof z.ZodError) {
      console.error('Validation errors:', error.errors);
    }
    throw new Error('Invalid API response format for action plan');
  }
};

export const fetchActionPlanAggregated = async (
  uuid: string,
  language?: string,
): Promise<ActionPlanAggregated> => {
  const queryParams = language ? `?language=${language}` : '';
  const data = await get(`/api/action-plan/public/surveys/${uuid}/action-plan/aggregated${queryParams}`);

  try {
    const validated = ActionPlanAggregatedSchema.parse(data);

    return {
      surveyId: validated.surveyId,
      categoryId: validated.categoryId,
      categoryName: validated.categoryName,
      participantCount: validated.participantCount,
      teamMemberCount: validated.teamMemberCount,
      interpretiveSummary: validated.summary ? {
        paragraphs: validated.summary.paragraphs.map(p =>
          typeof p === 'string' ? p : p.content
        )
      } : undefined,
      actionRecommendations: validated.actionRecommendations,
      pairedItems: validated.pairedItems,
      createdAt: validated.createdAt ? new Date(validated.createdAt) : new Date(),
      nextSurveyDate: validated.nextSurveyDate ? new Date(validated.nextSurveyDate) : undefined,
      baseline: validated.baseline,
      tenureSegments: validated.tenureSegments,
      roleSegments: validated.roleSegments,
    };
  } catch (error) {
    console.error('Action plan aggregated validation failed:', error);
    if (error instanceof z.ZodError) {
      console.error('Validation errors:', error.errors);
    }
    throw new Error('Invalid API response format for aggregated action plan');
  }
};
